import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import OptionSelector from '~/components/spec/OptionSelector';
import TradeoffPanel from '~/components/spec/TradeoffPanel';
import type { FeatureConfig } from '~/lib/builder-types';
import type { Feature } from '~/lib/db/schema';
import type { FeatureDefinition } from '~/lib/feature-modules/types';
import { COMPLEXITY_COLORS, cn } from '~/lib/utils';

interface Props {
  feature: Feature & { definition: FeatureDefinition };
  config: FeatureConfig;
  isRequired: boolean;
  dependentWarning?: string;
  onToggle: (slug: string, enabled: boolean) => void;
  onOptionChange: (
    slug: string,
    groupId: string,
    value: string | string[],
  ) => void;
}

export default function FeatureToggle({
  feature,
  config,
  isRequired,
  dependentWarning,
  onToggle,
  onOptionChange,
}: Props) {
  const [open, setOpen] = useState(config.enabled);

  function handleToggle() {
    onToggle(feature.slug, !config.enabled);
    if (!config.enabled) setOpen(true);
  }

  return (
    <div
      className={cn(
        'border rounded-xl overflow-hidden transition-colors',
        config.enabled
          ? 'border-indigo-200 bg-white'
          : 'border-gray-200 bg-gray-50',
      )}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={handleToggle}
          disabled={isRequired}
          className={cn(
            'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200',
            config.enabled ? 'bg-indigo-600' : 'bg-gray-300',
            isRequired && 'cursor-not-allowed opacity-60',
          )}
          title={isRequired ? 'This feature is required' : undefined}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200',
              config.enabled ? 'translate-x-4' : 'translate-x-0',
            )}
          />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'font-medium text-sm',
                config.enabled ? 'text-gray-900' : 'text-gray-500',
              )}
            >
              {feature.name}
            </span>
            {isRequired && (
              <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">
                Required
              </span>
            )}
            <span
              className={`text-xs border rounded-full px-1.5 py-0.5 ${COMPLEXITY_COLORS[feature.complexity]}`}
            >
              {feature.complexity}
            </span>
          </div>
          {dependentWarning && (
            <div className="flex items-center gap-1 mt-0.5">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              <p className="text-xs text-amber-700">{dependentWarning}</p>
            </div>
          )}
        </div>

        {config.enabled && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            {open ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {config.enabled && open && (
        <div className="border-t border-indigo-100 px-4 py-3 space-y-4">
          {feature.definition.optionGroups.map((group) => (
            <OptionSelector
              key={group.id}
              group={group}
              selected={config.options[group.id]}
              onChange={(gId, val) => onOptionChange(feature.slug, gId, val)}
            />
          ))}
          {feature.definition.tradeoffs.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Tradeoffs
              </p>
              <TradeoffPanel tradeoffs={feature.definition.tradeoffs} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
